/**
 * @file interceptor.ts
 * @description Factory function that creates an openapi-fetch-compatible middleware
 * for the API Visual Debugger.
 *
 * Usage:
 *   import { createDebugInterceptor } from '@/lib/api-debugger';
 *   client.use(createDebugInterceptor({ position: 'bottom-right', urlFilters: ['vrid'] }));
 *
 * CORS Safety:
 *   We intentionally avoid writing the tracking ID to HTTP headers.
 *   Instead we mutate the in-memory Request object: (request as any)._debugId = id
 *   This prevents the browser from sending a CORS preflight for a custom header.
 *
 * SSR / Client routing:
 *   - typeof window === 'undefined'  →  server environment  →  writes to serverStore
 *   - typeof window !== 'undefined'  →  browser environment →  writes to Zustand store
 *
 *   The import of `serverStore` at the top of this file is intentionally static.
 *   Next.js compiles separate server and client bundles; because this file is used
 *   server-side (via Server Components / server actions) the import is safe and
 *   will NOT be included in the browser bundle.
 */

import {
  addServerRequest,
  getServerRequests,
  updateServerRequest,
} from './serverStore';
import { useApiDebuggerStore } from './store';
import { ApiRequest, DebuggerConfig, DEFAULT_CONFIG } from './types';
import {
  captureStackTrace,
  formatStackFrames,
  matchesUrlFilters,
  parseStackTrace,
} from './utils';

import type { Middleware } from 'openapi-fetch';

// ─── Microtask-batched store writer ───────────────────────────────────────────

type Patch = Partial<Pick<ApiRequest, 'status' | 'statusCode' | 'duration'>>;

let pendingAdds: ApiRequest[] = [];
let pendingUpdates: Array<{ id: string; patch: Patch }> = [];
let flushScheduled = false;

// ─── Duplicate-instance safeguard ─────────────────────────────────────────────

const PROCESSED_SYMBOL: symbol =
    (globalThis as any).__apiDebuggerProcessedSymbol ??
    ((globalThis as any).__apiDebuggerProcessedSymbol = Symbol.for(
        '__apiDebuggerProcessed',
    ));

function scheduleClientFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  queueMicrotask(() => {
    flushScheduled = false;
    if (pendingAdds.length === 0 && pendingUpdates.length === 0) return;
    const adds = pendingAdds;
    const updates = pendingUpdates;
    pendingAdds = [];
    pendingUpdates = [];
    useApiDebuggerStore.getState().applyBatch(adds, updates);
  });
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createDebugInterceptor(
    config?: Partial<DebuggerConfig>,
): Middleware {
  const resolvedConfig: DebuggerConfig = { ...DEFAULT_CONFIG, ...config };

  // ── Disabled short-circuit ────────────────────────────────────────────────
  if (!resolvedConfig.enabled) {
    return {
      onRequest: ({ request }) => request,
      onResponse: ({ response }) => response,
    };
  }

  // Only seed the client Zustand store — the server has no UI to update.
  if (typeof window !== 'undefined') {
    (globalThis as any).__apiDebuggerClearOnReload = resolvedConfig.clearOnReload;

    useApiDebuggerStore.getState().setConfig(resolvedConfig);

    const persistApi = (useApiDebuggerStore as unknown as {
      persist?: { rehydrate: () => Promise<void> | void };
    }).persist;
    if (persistApi && !(globalThis as any).__apiDebuggerHydrated) {
      (globalThis as any).__apiDebuggerHydrated = true;
      void persistApi.rehydrate();
    }

    if (
        resolvedConfig.clearOnReload &&
        !(globalThis as any).__apiDebuggerClearedOnMount
    ) {
      (globalThis as any).__apiDebuggerClearedOnMount = true;
      useApiDebuggerStore.getState().clearRequests();
    }
  }

  return {
    // ── onRequest ────────────────────────────────────────────────────────────
    onRequest({ request }) {
      if ((request as any)[PROCESSED_SYMBOL]) return request;
      (request as any)[PROCESSED_SYMBOL] = true;

      if (!matchesUrlFilters(request.url, resolvedConfig.urlFilters)) {
        return request;
      }

      // 1. Захоплюємо стек ДО перевірки на logToConsole
      const rawStack = captureStackTrace();

      // 2. Виводимо наш очищений стек замість console.trace()
      if (resolvedConfig.logToConsole) {
        console.groupCollapsed(`📡 API: ${request.method} ${request.url}`);
        console.trace('Trace');
        console.groupEnd();
      }

      const isServer = typeof window === 'undefined';
      const id = generateId();

      const entry: ApiRequest = {
        id,
        url: request.url,
        method: request.method,
        status: 'pending',
        statusCode: null,
        startedAt: Date.now(),
        duration: null,
        environment: isServer ? 'SSR' : 'Client',
        stackTrace: rawStack,
        stackFrames: undefined,
        trigger: undefined,
      };

      if (isServer) {
        // ── SSR path: write to the global server-side cache ──────────────────
        addServerRequest(entry, resolvedConfig.maxRequests);
      } else {
        // ── Client path: queue for microtask-batched flush ───────────────────
        pendingAdds.push(entry);
        scheduleClientFlush();
      }

      // Зберігаємо ID у request для onResponse
      (request as any)._debugId = id;

      return request;
    },

    // ── onResponse ───────────────────────────────────────────────────────────
    onResponse({ request, response }) {
      // Дістаємо ID, який ми зберегли в onRequest
      const id = (request as any)._debugId as string | undefined;

      // Якщо ID немає, значить запит був відфільтрований — просто повертаємо response
      if (!id) return response;

      const isServer = typeof window === 'undefined';

      if (isServer) {
        // ── SSR path: look up startedAt from serverStore, then patch it ──────
        const startedAt =
            getServerRequests().find((r) => r.id === id)?.startedAt ?? Date.now();

        updateServerRequest(id, {
          status: response.ok ? 'success' : 'error',
          statusCode: response.status,
          duration: Date.now() - startedAt,
        });
      } else {
        // ── Client path: look up startedAt from the live store OR the
        // pending-adds buffer
        const pendingEntry = pendingAdds.find((r) => r.id === id);
        const startedAt =
            pendingEntry?.startedAt ??
            useApiDebuggerStore.getState().requests.find((r) => r.id === id)
                ?.startedAt ??
            Date.now();

        pendingUpdates.push({
          id,
          patch: {
            status: response.ok ? 'success' : 'error',
            statusCode: response.status,
            duration: Date.now() - startedAt,
          },
        });
        scheduleClientFlush();
      }

      return response;
    },
  };
}